import React, { useState } from "react";
import roadmap from "@content/schedule.json";

const stepsAfter = [
    { n: "01", title: "Reserve Your Seat", desc: "Tap the button and pick your batch." },
    { n: "02", title: "Secure Razorpay Payment", desc: "Pay ₹799 safely — cards, UPI or wallets." },
    { n: "03", title: "Instant WhatsApp Invite", desc: "You're added to your private batch group." },
    { n: "04", title: "Zoom Link", desc: "Your live session link, ready and waiting." },
    { n: "05", title: "Installation Guide", desc: "Simple setup so you're ready for Day 1." },
    { n: "06", title: "Monday Live Session", desc: "Your journey begins, live with your mentor." },
    { n: "07", title: "Enterprise Labs", desc: "Build and troubleshoot real networks." },
    { n: "08", title: "Career Roadmap", desc: "Leave with a clear path to your next role." },
];

const Roadmap = () => {
    const [active, setActive] = useState(0);
    const day = roadmap[active];

    return (
        <>
            {/* 7-Day Journey */}
            <section className="section" id="roadmap" data-testid="roadmap-section">
                <div className="container-p">
                    <div className="max-w-3xl">
                        <span className="eyebrow">Your 7-Day Journey</span>
                        <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-tight text-slate-900">
                            What You'll Experience During{" "}
                            <span className="text-brand">the 7 Days.</span>
                        </h2>
                        <p className="mt-4 text-dim text-base md:text-lg">
                            No boring theory. No slides you forget. Every single day is live, hands-on,
                            and builds your confidence step by step.
                        </p>
                    </div>

                    <div className="mt-6 grid lg:grid-cols-[220px_1fr] gap-4">
                        {/* Day picker */}
                        <div
                            className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-2 px-2"
                            role="tablist"
                        >
                            {roadmap.map((d, i) => (
                                <button
                                    key={d.day}
                                    role="tab"
                                    aria-selected={active === i}
                                    onClick={() => setActive(i)}
                                    className={`text-left shrink-0 min-w-[110px] lg:min-w-0 px-4 py-3 rounded-lg border font-mono text-sm transition-all ${
                                        active === i
                                            ? "border-[rgba(124,231,59,0.6)] bg-[rgba(124,231,59,0.08)] text-brand"
                                            : "border-[var(--border)] bg-slate-50 text-dim hover:text-slate-900 hover:border-[var(--text-mute)]"
                                    }`}
                                    data-testid={`day-tab-${i + 1}`}
                                >
                                    <div className="font-bold">{d.day}</div>
                                    <div className="text-[11px] text-mute truncate mt-0.5">
                                        {d.duration}
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Day detail */}
                        <div className="panel p-5 md:p-6" data-testid={`day-panel-${active + 1}`}>
                            <div className="font-mono text-xs uppercase tracking-widest text-brand">
                                {day.day} Syllabus · {day.duration}
                            </div>
                            <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
                                {day.title}
                            </h3>
                            <p className="mt-3 text-dim leading-relaxed">{day.description}</p>

                            <div className="mt-4 rounded-lg border border-[rgba(124,231,59,0.28)] bg-[rgba(124,231,59,0.05)] p-3">
                                <div className="font-mono text-[11px] uppercase tracking-widest text-brand">
                                    Live Hands-On Lab Spotlight
                                </div>
                                <div className="mt-2 text-slate-900">{day.lab}</div>
                            </div>

                            <div className="mt-4">
                                <div className="font-mono text-[11px] uppercase tracking-widest text-mute mb-2">
                                    What You Will Master
                                </div>
                                <ul className="grid sm:grid-cols-3 gap-2">
                                    {day.master.map((m) => (
                                        <li
                                            key={m}
                                            className="flex items-start gap-2 text-sm text-dim rounded-md border border-[var(--border)] bg-slate-50 px-3 py-1.5"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" className="text-brand shrink-0 mt-0.5" aria-hidden>
                                                <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                            </svg>
                                            <span>{m}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What Happens After You Pay */}
            <section className="section pt-0" id="after-pay" data-testid="after-pay-section">
                <div className="container-p">
                    <div className="panel p-5 md:p-8 relative overflow-hidden">
                        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" aria-hidden />
                        <div className="relative">
                            <div className="max-w-3xl">
                                <span className="eyebrow gray">No Confusion, No Guesswork</span>
                                <h2 className="mt-4 font-display text-2xl md:text-4xl font-extrabold leading-tight text-slate-900">
                                    What Happens After You Pay?
                                </h2>
                                <p className="mt-3 text-dim">
                                    Everything is handled for you, step by step. You'll know exactly what
                                    comes next from the moment you reserve your seat.
                                </p>
                            </div>
                            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {stepsAfter.map((s) => (
                                    <div key={s.n} className="panel panel-hover p-3">
                                        <div className="font-mono text-brand text-xs tracking-widest">{s.n}</div>
                                        <div className="mt-2 font-bold text-slate-900 text-base leading-tight">
                                            {s.title}
                                        </div>
                                        <div className="mt-1 text-sm text-dim leading-snug">
                                            {s.desc}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Roadmap;
